'use client';

import { Divider, Stack, TextField, Theme, Typography } from '@mui/material';
import { withClarityTheme } from '@verifime/styles';
import { ReactElement, ReactNode } from 'react';
import CircularProgressButton from '../Form/CircularProgressButton';
import SignInWithGoogleButton from '../Button/SignInWithGoogleButton';
import { ClarityNotice } from '../ClarityNotice/ClarityNotice';
import { LinkSent } from '../LinkSent/LinkSent';
import { LINK_SENT_TAIL } from '../MagicLinkCopy/linkSentCopy';

export type TGoogleSsoSignUpFormProps = {
  display: 'ENTER_EMAIL' | 'SENT_EMAIL';
  companyName: string;
  companyLogo: ReactNode;
  emailLocked: boolean;
  email: string;
  onEmailChange: (email: string) => void;
  onContinue: () => void;
  isSubmitting: boolean;
  onGoogle: () => void;
  googleLoading: boolean;
  error?: string;
  /** Optional slot rendered on the SENT_EMAIL screen for a resend affordance (e.g.
   *  LinkSentResend). Caller-supplied, following the same rendered-ReactNode-slot pattern as
   *  `companyLogo` above; this form places it, but never constructs it. Forwarded straight into
   *  LinkSent's own `resend` slot. */
  resendAction?: ReactNode;
  /** When the magic link this screen reports on stops working. Only meaningful once `display` is
   *  SENT_EMAIL, but required unconditionally (matching `email`'s own contract): only the caller
   *  that fired the send knows when that was (see GoogleSsoSignUpView's own doc comment on
   *  `expiresAt`), and this form must never derive it itself (that would recompute from "now" on
   *  every render, creeping the shown expiry forward as the customer watches). Forwarded straight
   *  into LinkSent's own `expiresAt`. */
  expiresAt: Date | string;
  'data-testid'?: string;
};

function GoogleInstruction({
  emailLocked,
  email,
  companyName,
}: {
  emailLocked: boolean;
  email: string;
  companyName: string;
}): ReactElement {
  const text = emailLocked
    ? `Choose the Google account for ${email}, the address ${companyName.trim() || 'your provider'} registered. A different account cannot be verified.`
    : 'Use the Google account for the email your provider registered. A different account cannot be verified.';

  return (
    <Typography variant="body2" sx={(theme: Theme) => ({ color: theme.palette.text.tertiary })}>
      {text}
    </Typography>
  );
}

function GoogleSsoSignUpFormComponent({
  display,
  companyName,
  companyLogo,
  emailLocked,
  email,
  onEmailChange,
  onContinue,
  isSubmitting,
  onGoogle,
  googleLoading,
  error,
  resendAction,
  expiresAt,
  'data-testid': dataTestId,
}: Readonly<TGoogleSsoSignUpFormProps>): ReactElement {
  if (display === 'SENT_EMAIL') {
    // Task 3 ("Link sent, one screen", settled 2026-09-17): this branch's previous "Check your
    // email" ClarityNotice is superseded and gone, replaced by the shared LinkSent shell every
    // "we sent you a magic link" screen now composes. Every visible string below is one of the
    // approved copy, inlined directly; this form authors none of it.
    return (
      <Stack
        data-testid={dataTestId}
        sx={(theme: Theme) => ({
          width: '100%',
          maxWidth: theme.semantic.size.xs,
          gap: theme.semantic.spacing.base,
          alignItems: 'center',
          textAlign: 'center',
        })}
      >
        <LinkSent
          heading="Email sent!"
          leadLine="We've sent a link to"
          email={email}
          expiryLabel="The email link will be valid until"
          expiresAt={expiresAt}
          tail={LINK_SENT_TAIL}
          resend={resendAction}
        />
        {error && (
          <ClarityNotice severity="danger" data-testid="signup-error-notice">
            {error}
          </ClarityNotice>
        )}
      </Stack>
    );
  }

  return (
    <Stack
      sx={(theme: Theme) => ({
        width: '100%',
        maxWidth: theme.semantic.size.xs,
        gap: theme.semantic.spacing.lg,
      })}
    >
      <Typography variant="h5" textAlign="center">
        Welcome to VerifiMe
      </Typography>
      {companyLogo}

      <Stack
        component="form"
        onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
          e.preventDefault();
          onContinue();
        }}
        sx={(theme: Theme) => ({ gap: theme.semantic.spacing.base })}
      >
        <Stack sx={(theme: Theme) => ({ gap: theme.semantic.spacing.xs })}>
          <Typography variant="h6">Continue with your email</Typography>
          <TextField
            fullWidth
            type="email"
            autoFocus
            autoComplete="webauthn"
            placeholder="name@example.com"
            value={email}
            onChange={(e) => onEmailChange(e.target.value)}
            disabled={isSubmitting}
            inputProps={{ readOnly: emailLocked, 'data-field': 'email', 'aria-label': 'Email' }}
          />
        </Stack>
        {emailLocked && (
          <Typography
            variant="body2"
            sx={(theme: Theme) => ({ color: theme.palette.text.tertiary })}
          >
            From your invitation, this cannot be changed.
          </Typography>
        )}
        <CircularProgressButton
          fullWidth
          size="large"
          color="primary"
          type="submit"
          variant="contained"
          disabled={!email}
          isShowProgress={isSubmitting}
        >
          Continue
        </CircularProgressButton>
      </Stack>

      <Divider>
        <Typography variant="body2" sx={(theme: Theme) => ({ color: theme.palette.text.tertiary })}>
          OR
        </Typography>
      </Divider>

      <Stack sx={(theme: Theme) => ({ gap: theme.semantic.spacing.sm })}>
        <Typography variant="h6">Continue with Google</Typography>
        <GoogleInstruction emailLocked={emailLocked} email={email} companyName={companyName} />
        <SignInWithGoogleButton fullWidth onClick={onGoogle} loading={googleLoading} />
      </Stack>

      {error && (
        <ClarityNotice severity="danger" data-testid="signup-error-notice">
          {error}
        </ClarityNotice>
      )}
    </Stack>
  );
}

export const GoogleSsoSignUpForm = withClarityTheme(GoogleSsoSignUpFormComponent);
